import type { CheckDepthTier } from '@nestjs-boilerplate/types';
import { Check } from 'lucide-react';

import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { cn } from '@/lib/utils';

import type { TierConfig } from '../constants/tier-config';
import { TIER_CONFIGS } from '../constants/tier-config';

interface TierCardProps {
  tier: TierConfig;
  isSelected: boolean;
  onSelect: (tier: CheckDepthTier) => void;
}

/**
 * Tier selection card using shadcn components following pricing block patterns.
 */
export function TierCard({ tier, isSelected, onSelect }: TierCardProps) {
  const handleClick = () => {
    onSelect(tier.id);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onSelect(tier.id);
    }
  };

  const parentTierName = tier.includesFrom ? TIER_CONFIGS[tier.includesFrom].name : null;

  return (
    <Card
      role="radio"
      aria-checked={isSelected}
      tabIndex={0}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      className={cn(
        'relative flex h-full cursor-pointer flex-col transition-all',
        isSelected
          ? 'border-[#131313] ring-1 ring-[#131313]'
          : 'border-[#E5E7EB] hover:border-gray-300',
      )}
    >
      {/* Selection indicator */}
      <div
        className={cn(
          'absolute right-4 top-4 flex h-5 w-5 items-center justify-center rounded-full border-2 transition-colors',
          isSelected ? 'border-[#131313] bg-[#131313]' : 'border-gray-300 bg-white',
        )}
      >
        {isSelected && <Check className="h-3 w-3 text-white" strokeWidth={3} />}
      </div>

      <CardHeader className="text-center">
        <CardTitle className="text-lg font-semibold text-[#051923]">{tier.name}</CardTitle>
        <CardDescription className="text-sm">{tier.description}</CardDescription>
      </CardHeader>

      <CardContent className="flex-1 space-y-6">
        {/* Price */}
        <div className="text-center">
          <span className="text-4xl font-bold tracking-tight text-[#131313]">{tier.price}</span>
          <span className="ml-1 text-sm text-muted-foreground">/check</span>
        </div>

        <Separator />

        {/* Features */}
        <div className="space-y-3">
          {parentTierName && (
            <p className="text-sm font-medium text-muted-foreground">
              Everything in {parentTierName}, plus:
            </p>
          )}

          {tier.features.map((feature) => (
            <div key={feature.text} className="flex items-start gap-3">
              <div className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-green-100">
                <Check className="h-3 w-3 text-green-600" strokeWidth={3} />
              </div>
              <span className="text-sm text-[#051923]">{feature.text}</span>
            </div>
          ))}
        </div>
      </CardContent>

      <CardFooter className="pt-4">
        <Button
          type="button"
          variant={isSelected ? 'default' : 'outline'}
          onClick={(e) => {
            e.stopPropagation();
            onSelect(tier.id);
          }}
          className={cn(
            'h-11 w-full text-sm font-medium',
            isSelected
              ? 'bg-[#131313] text-white hover:bg-[#131313]/90'
              : 'border-[#131313] text-[#131313] hover:bg-gray-50',
          )}
        >
          {isSelected ? (
            <span className="flex items-center gap-2">
              <Check className="h-4 w-4" />
              Selected
            </span>
          ) : (
            'Select Plan'
          )}
        </Button>
      </CardFooter>
    </Card>
  );
}
